import React, { useEffect, useRef, memo, useState } from "react";
import mapboxgl from "mapbox-gl";
import axios from "axios";
import "mapbox-gl/dist/mapbox-gl.css";
import { mapBoxToken } from "../config";

const fetchMapStyleData = async (styleUrl) => {
    try {
        if (!styleUrl || !styleUrl.startsWith('mapbox://styles/')) return null;
        const response = await axios.get(
            `https://api.mapbox.com/styles/v1/${styleUrl.substring('mapbox://styles/'.length)}?access_token=${mapBoxToken}`
        );
        return response?.data || null;
    } catch (error) {
        console.error("Error fetching map style data:", error);
        return null;
    }
};

const MapBoxPointComponent = ({
    points = [],
    onPointClick,
    selectedPointId = null,
    initialZoom = 5,
    mapStyle = "mapbox://styles/amjp20/clp27a1k0009e01qterka7hfw",
    zoomPadding = 50,
    clickZoom = 14,
}) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const [mapLoading, setMapLoading] = useState(true);
    const [mapError, setMapError] = useState(null);

    const handleMarkerClick = (point) => {
        if (onPointClick) {
            onPointClick(point);
        }
        mapRef.current.jumpTo({
            center: [point.longitude, point.latitude],
            zoom: clickZoom,
            duration: 50,
            offset: [0, -zoomPadding],
        });
    };

    useEffect(() => {
        if (!mapContainerRef.current) return;

        const initializeMap = async () => {
            try {
                setMapLoading(true);
                setMapError(null);

                const styleData = await fetchMapStyleData(mapStyle);
                const defaultCenter = styleData?.center || [0, 20];
                mapboxgl.accessToken = mapBoxToken;
                mapRef.current = new mapboxgl.Map({
                    container: mapContainerRef.current,
                    style: mapStyle,
                    center: defaultCenter,
                    zoom: initialZoom,
                });
                mapRef.current.addControl(new mapboxgl.NavigationControl());
                mapRef.current.on('load', () => {
                    mapRef.current.resize();
                });

                setMapLoading(false);

            } catch (error) {
                console.error("Map initialization error:", error);
                setMapError("Failed to load map");
                setMapLoading(false);
            }
        };

        initializeMap();

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [mapStyle, initialZoom]);

    useEffect(() => {
        if (!mapRef.current || !points.length || mapLoading) return;
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        points.forEach(point => {
            if (point.longitude == null || point.latitude == null) return;

            const marker = new mapboxgl.Marker({
                color: selectedPointId === point.opportunityID ? '#ED6C2D' : 'rgba(237, 237, 237, 1)',
            })
                .setLngLat([point.longitude, point.latitude])
                .addTo(mapRef.current);

            marker.getElement().addEventListener('click', () => {
                handleMarkerClick(point);
            });

            markersRef.current.push(marker);
        });
        if (points.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            points.forEach(point => {
                if (point.longitude != null && point.latitude != null) {
                    bounds.extend([point.longitude, point.latitude]);
                }
            });
            if (!bounds.isEmpty()) {
                mapRef.current.fitBounds(bounds, { padding: zoomPadding });
            }
        }

    }, [points, selectedPointId, mapLoading, zoomPadding, clickZoom]);

    if (mapError) {
        return <div className="map-error">Error loading map: {mapError}</div>;
    }

    return (
        <div
            ref={mapContainerRef}
            style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                width: "100%",
                height: "100%"
            }}
        >
            {mapLoading && <div className="map-loading">Loading map...</div>}
        </div>
    );
};

export default memo(MapBoxPointComponent);